import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { TrainingQuestionDef } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { QuestionCommonFields } from './QuestionCommonFields';
import { genId } from './moduleOps';

type OrderQuestion = Extract<TrainingQuestionDef, { type: 'ordering' }>;

/**
 * Items editor for an ordering question. The list as authored here IS the
 * correct order; the player shuffles it for the inductee.
 */
export function OrderQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: OrderQuestion;
  onChange: (next: OrderQuestion) => void;
  /** False hides the points box (an unmarked assessment). */
  graded?: boolean;
}) {
  function move(index: number, dir: -1 | 1) {
    const to = index + dir;
    if (to < 0 || to >= question.items.length) return;
    const items = [...question.items];
    const [item] = items.splice(index, 1);
    items.splice(to, 0, item!);
    onChange({ ...question, items });
  }

  return (
    <div className="space-y-3">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
      />
      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">
          Steps, in the CORRECT order (shuffled for the inductee)
        </span>
        {question.items.map((item, i) => (
          <div key={item.id} className="flex items-center gap-2">
            <span className="w-5 text-xs font-semibold tabular-nums text-fg-muted">{i + 1}.</span>
            <input
              value={item.label}
              onChange={(e) =>
                onChange({
                  ...question,
                  items: question.items.map((x) =>
                    x.id === item.id ? { ...x, label: e.target.value } : x,
                  ),
                })
              }
              placeholder="Step text"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <button
              type="button"
              aria-label="Move up"
              disabled={i === 0}
              onClick={() => move(i, -1)}
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={i === question.items.length - 1}
              onClick={() => move(i, 1)}
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Remove step"
              disabled={question.items.length <= 2}
              onClick={() =>
                onChange({ ...question, items: question.items.filter((x) => x.id !== item.id) })
              }
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({ ...question, items: [...question.items, { id: genId('item'), label: '' }] })
          }
        >
          Add step
        </Button>
      </div>
    </div>
  );
}
